import React from 'react';
import { DndProvider } from 'react-dnd';
import { MultiBackend } from 'react-dnd-multi-backend';
import { createRoot } from 'react-dom/client';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ToastProvider } from 'react-toast-notifications';
import { IntercomProvider } from 'react-use-intercom';

import { DrawerProvider } from 'common/utilities/Drawer/DrawerContext';
import ErrorBoundary from 'components/common/ErrorBoundary';
import configuration from 'configs/configuration';
import { multiBackendOptions } from 'configs/dndConfig';
import { AntdProvider } from 'helper/AntdProvider';
import { CustomRouter } from 'helper/customRouter';
import { history } from 'helper/customRouter/history';
import ScrollToTop from 'helper/ScrollToTop';
import i18n, { loadLanguage } from 'locales/i18n';
import AuthProvider from 'services/auth/AuthContext';
import * as Logger from 'services/logger';
import { SignalRProvider } from 'services/signalr/SignalRContext';
import { ThemeProvider } from 'services/theme';
import TooltipsProvider from 'services/tooltips';

import App from './App';

Logger.init();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 10,
    },
  },
});

const container = document.getElementById('root');
const root = createRoot(container);

const app = (
  <AntdProvider>
    <IntercomProvider appId={configuration.intercomAppId}>
      <HelmetProvider>
        <CustomRouter history={history}>
          <ThemeProvider>
            <ErrorBoundary>
              <DndProvider backend={MultiBackend} options={multiBackendOptions}>
                <TooltipsProvider>
                  <AuthProvider>
                    <ToastProvider autoDismissTimeout={5000} transitionDuration={220}>
                      <DrawerProvider>
                        <QueryClientProvider client={queryClient}>
                          <GoogleReCaptchaProvider
                            reCaptchaKey={configuration.REACT_APP_RECAPTCHA_SITE_KEY}
                            scriptProps={{ async: true, appendTo: 'head' }}
                          >
                            <ScrollToTop />
                            <SignalRProvider>
                              <App />
                            </SignalRProvider>
                            <ReactQueryDevtools initialIsOpen />
                          </GoogleReCaptchaProvider>
                        </QueryClientProvider>
                      </DrawerProvider>
                    </ToastProvider>
                  </AuthProvider>
                </TooltipsProvider>
              </DndProvider>
            </ErrorBoundary>
          </ThemeProvider>
        </CustomRouter>
      </HelmetProvider>
    </IntercomProvider>
  </AntdProvider>
);

loadLanguage(i18n.language).finally(() => root.render(app));

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(error => {
      console.log('Service worker registration failed:', error);
    });
  });
}
